import React, { useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { useClientId } from '../../../../_hooks/useClientId';
import { FormFields } from '../AccountPreferences/components/AccountForm/AccountForm.meta';
import { DeleteHoldingModal } from './components/DeleteHoldingModal/DeleteHoldingModal';
import { EditHoldingsModal } from './components/EditHoldingsModal/EditHoldingsModal';
import { HoldingsTable } from './components/HoldingsTable/HoldingsTable';
import { UpsertHoldingModal } from './components/UpsertHoldingModal/UpsertHoldingModal';
import { useHoldings } from './useHoldings';
import { AllocationTaxStatusDTO, TaxStatusDTO } from '../../api/useAccountsQuery';

export const Holdings = () => {
  const clientId = useClientId();

  const {
    data: {
      defaultAssetAllocations,
      defaultAssetBucketIndex,
      availableAssetBuckets,
      isLinkedAccount,
      isOrionManagedAccount,
    },
  } = useHoldings();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [holdingIndexToUpsert, setHoldingIndexToUpsert] = useState<number | null>(null);

  const [holdingToDelete, setHoldingToDelete] = useState<string | null>(null);

  const holdingsArrayFormBag = useFieldArray<FormFields, 'bankAccount.Positions', 'key'>({
    name: 'bankAccount.Positions',
    keyName: 'key',
  });

  const { fields: holdings, append: appendHolding } = holdingsArrayFormBag;

  return (
    <>
      <Box p="16">
        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-end">
          {holdings.length > 0 && (
            <Text fontWeight={600} mr="auto">
              {holdings.length} Holdings
            </Text>
          )}

          {!isLinkedAccount && !isOrionManagedAccount && (
            <Button
              variant="subtleActive"
              leftIcon="fas fa-plus"
              onClick={() => {
                const defaultValues = {
                  ProductId: null,
                  Ticker: '',
                  Name: '',
                  Quantity: null,
                  Price: null,
                  IsPriceManuallyEntered: false,
                  AssetBucketIndex: defaultAssetBucketIndex,
                  AssetAllocation: defaultAssetAllocations,
                  TaxStatus: TaxStatusDTO.PreTax as AllocationTaxStatusDTO,
                };

                appendHolding(defaultValues);

                setHoldingIndexToUpsert(holdings.length);
              }}
            >
              Add Holding
            </Button>
          )}

          {holdings.length > 0 && (
            <Button
              variant="subtleActive"
              leftIcon="far fa-pencil"
              onClick={() => setIsEditModalOpen(true)}
              ml="8"
            >
              Edit Holdings
            </Button>
          )}
        </Box>
      </Box>

      <HoldingsTable
        holdings={holdings}
        readonly
        availableAssetBuckets={availableAssetBuckets}
        onEditHolding={setHoldingIndexToUpsert}
        onDeleteHolding={setHoldingToDelete}
      />

      <EditHoldingsModal
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        holdings={holdings}
        availableAssetBuckets={availableAssetBuckets}
        onEditHolding={setHoldingIndexToUpsert}
        onDeleteHolding={setHoldingToDelete}
      />

      {holdingIndexToUpsert !== null && (
        <UpsertHoldingModal
          holdingIndex={holdingIndexToUpsert}
          holdingsArrayFormBag={holdingsArrayFormBag}
          availableAssetBuckets={availableAssetBuckets}
          onClose={() => setHoldingIndexToUpsert(null)}
        />
      )}

      {holdingToDelete && (
        <DeleteHoldingModal
          clientId={clientId}
          holdingId={holdingToDelete}
          holdingsArrayFormBag={holdingsArrayFormBag}
          onClose={() => {
            setHoldingToDelete(null);

            if (holdings.length === 1) {
              setIsEditModalOpen(false);
            }
          }}
        />
      )}
    </>
  );
};
